import React, { FC, useMemo, useState } from 'react'
import { Tabs, Card, Typography, Switch } from 'antd'
import EditableDocumentText from '../EditableDocumentText'
import { ICaseTextGroup, IDatahubCase } from '@libs/api'
import { useLocalization } from '@libs/localization'
import { Container } from '@libs/components'

interface TextDocumentsProps {
	caseId: IDatahubCase['id']
	translatedTexts: ICaseTextGroup[]
}

const TextDocuments: FC<TextDocumentsProps> = ({ caseId, translatedTexts }) => {
	const { t } = useLocalization()
	const [isShowingAllSections, setIsShowingAllSections] = useState(false)
	const [selectedGroupName, setSelectedGroupName] = useState<string>()

	// Every case carries the full section catalogue, delivered or not, so by default only sections with content are shown.
	const visibleGroups = useMemo(
		() =>
			isShowingAllSections
				? translatedTexts
				: translatedTexts
						.map(group => ({ ...group, texts: group.texts.filter(text => text.content) }))
						.filter(group => group.texts.length > 0),
		[translatedTexts, isShowingAllSections]
	)

	const isSelectedGroupVisible = visibleGroups.some(({ groupName }) => groupName === selectedGroupName)
	const activeGroupName = isSelectedGroupVisible ? selectedGroupName : visibleGroups[0]?.groupName

	return (
		<Card
			title={t('common.terms.textDocuments')}
			extra={
				<Container display={'flex'} alignItems={'center'} space={'sm'} spaceDirection={'horizontal'}>
					<Typography.Text>{t('cases.pages.details.showAllTextSections')}</Typography.Text>
					<Switch checked={isShowingAllSections} onChange={setIsShowingAllSections} />
				</Container>
			}
		>
			{visibleGroups.length ? (
				<Tabs activeKey={activeGroupName} onChange={setSelectedGroupName}>
					{visibleGroups.map(({ groupName, texts }) => (
						<Tabs.TabPane tab={groupName} key={groupName}>
							<Container space={'md'} spaceDirection={'vertical'}>
								{texts.map(item => (
									<EditableDocumentText key={item.name} caseId={caseId} {...item} />
								))}
							</Container>
						</Tabs.TabPane>
					))}
				</Tabs>
			) : (
				<Typography.Text>{t('cases.pages.details.emptyTexts')}</Typography.Text>
			)}
		</Card>
	)
}

export default TextDocuments
